import type { RowData, RowSelectionState } from "@tanstack/react-table";
import type { CSSProperties } from "react";
import React from "react";
import { createColumnHelper } from "./column-helper";
import { DataTable } from "./data-table";
import { createMeta } from "./handlers";
import type { DataTableVariant } from "./shared";
import { SubDataTable } from "./sub-data-table";
import type { CellContext, Column } from "./types";

/**
 * Sticky offsets for a pinned column. Pinning is logical — `start` and `end` — and this
 * left-to-right layout renders `start` on the left, which is where the CSS hooks
 * (`data-pinned-left`, `left:`) get their physical names.
 */
export function getCommonPinningStyles<T extends RowData>(column: Column<T>): CSSProperties {
	const isPinned = column.getIsPinned();

	return {
		left: isPinned === "start" ? `${column.getStart("start")}px` : undefined,
		right: isPinned === "end" ? `${column.getAfter("end")}px` : undefined,
		position: isPinned ? "sticky" : "relative",
		zIndex: isPinned ? 1 : 0,
		overflow: "hidden",
		width: `calc(var(--header-${column.id}-size) * 1px)`,
	};
}

export function createRowActions<T extends RowData>(
	rowActions: (props: CellContext<T, "actions">) => React.ReactElement,
) {
	const columnHelper = createColumnHelper<T>();
	return columnHelper.accessor((_, i) => `Index ${i}`, {
		id: "actions",
		header: "",
		cell: rowActions,
		enableSorting: false,
		enableHiding: false,
		enablePinning: true,
		enableResizing: false,
		minSize: 100,
		meta: createMeta({
			isNumeric: true,
			hideOptions: true,
		}),
	});
}

/**
 * This function is used to flatten the row selection.
 */
export function getFlatRowSelection(rowSelectionState: RowSelectionState) {
	return Object.keys(rowSelectionState).filter((id) => rowSelectionState[id]);
}

/**
 * This function is used to create the initial row selection state based on a set of ids.
 */
export function getInitialRowSelectionState(ids: string[]): RowSelectionState {
	const state: RowSelectionState = {};
	for (const id of ids) {
		state[id] = true;
	}
	return state;
}

export type RowActionsProps<T extends RowData> = CellContext<T, "actions">;

export function getDataTableComponent(variant: DataTableVariant) {
	if (variant === "sub-table") return SubDataTable;
	return DataTable;
}
